import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  Box, Typography, Grid, Button, Chip, Divider,
  IconButton, Skeleton, Paper, Breadcrumbs, Link, Alert, Container, LinearProgress, useMediaQuery
} from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import InventoryIcon from '@mui/icons-material/Inventory';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import HomeIcon from '@mui/icons-material/Home';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import api from '../services/api';
import { useCart } from '../context/CartContext';
import { useStore } from '../context/StoreContext';
import SEO from '../components/SEO';
import SocialShare from '../components/SocialShare';
import { RelatedProducts } from '../components/Recommendations';
import useBehaviorTracking from '../hooks/useBehaviorTracking';
import { trackTikTokViewContent, trackTikTokAddToCart } from '../components/TikTokPixel';
import { useTheme } from '@mui/material/styles';
import StarRating from '../components/StarRating';
import ProductReviews from '../components/ProductReviews';
import ReviewForm from '../components/ReviewForm';
import RateReviewIcon from '@mui/icons-material/RateReview';
import { getTemplateStyles, formatDiscountBadge } from '../config/templateStyles';
import { normalizeImageUrl, onImageErrorSetFallback } from '../utils/imageFallback';

const ProductDetail = () => {
  const { t } = useTranslation('product');
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewSummary, setReviewSummary] = useState(null);
  const [countdown, setCountdown] = useState({ hours: 0, minutes: 0, seconds: 0 });
  const { templateId } = useStore();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { trackView, trackCartAdd } = useBehaviorTracking();
  const tplStyles = getTemplateStyles(templateId);

  // Countdown to end-of-day (for urgency UI).
  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
      const diff = endOfDay - now;
      if (diff > 0) {
        return {
          hours: Math.floor(diff / (1000 * 60 * 60)),
          minutes: Math.floor((diff / (1000 * 60)) % 60),
          seconds: Math.floor((diff / 1000) % 60)
        };
      }
      return { hours: 0, minutes: 0, seconds: 0 };
    };

    setCountdown(calculateTimeLeft());
    const timer = setInterval(() => {
      setCountdown(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const parseImages = (product) => {
    const images = [];
    // 1) Primary image.
    if (product.image_url) {
      images.push(normalizeImageUrl(product.image_url, product.name));
    }
    // 2) Additional images (JSON array).
    if (product.images) {
      try {
        const additionalImages = typeof product.images === 'string'
          ? JSON.parse(product.images)
          : product.images;
        if (Array.isArray(additionalImages)) {
          additionalImages.forEach(img => {
            const fixedImg = normalizeImageUrl(img, product.name);
            if (fixedImg && !images.includes(fixedImg)) {
              images.push(fixedImg);
            }
          });
        }
      } catch (e) {
        console.log('Failed to parse product.images:', e);
      }
    }
    // 3) Extract images from HTML description (best effort).
    if (product.description) {
      const decoded = product.description
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&amp;/g, '&')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'");
      const imgRegex = /src\s*=\s*['"]([^'"]+)['"]/g;
      let match;
      while ((match = imgRegex.exec(decoded)) !== null) {
        let imgUrl = match[1];
        // Vendor-specific path fix.
        if (imgUrl.startsWith('/image/')) {
          imgUrl = `https://www.ymshop.com.tw${imgUrl}`;
        }
        if (!images.includes(imgUrl)) {
          images.push(imgUrl);
        }
      }
    }
    return images.length > 0 ? images : [normalizeImageUrl('', product.name)];
  };

  const parseSpecs = (specsJson) => {
    if (!specsJson) return [];
    try {
      const parsed = typeof specsJson === 'string' ? JSON.parse(specsJson) : specsJson;
      if (!Array.isArray(parsed)) return [];
      return parsed.map(spec => {
        if (spec.option_name && Array.isArray(spec.values)) return spec;
        if (spec.name && !spec.values) {
          return { option_name: t('detail.specs'), values: [{ name: spec.name, price: 0 }], _grouped: true };
        }
        if (typeof spec === 'string') {
          return { option_name: t('detail.specs'), values: [{ name: spec, price: 0 }], _grouped: true };
        }
        return null;
      }).filter(Boolean)
        .reduce((acc, spec) => {
          if (spec._grouped) {
            const existing = acc.find(s => s.option_name === spec.option_name);
            if (existing) {
              existing.values.push(...spec.values);
              return acc;
            }
          }
          return [...acc, spec];
        }, []);
    } catch {
      return [];
    }
  };

  const getOptionsPrice = () => {
    if (!product?.specs) return 0;
    const specs = parseSpecs(product.specs);
    let extraPrice = 0;
    specs.forEach(spec => {
      const selected = selectedOptions[spec.option_name];
      if (selected) {
        const option = (spec.values || []).find(v => v.name === selected);
        if (option && option.prefix === '+') {
          extraPrice += parseFloat(option.price) || 0;
        }
      }
    });
    return extraPrice;
  };

  // Scroll to top when product ID changes.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await api.get(`/products/${id}`);
        setProduct(response.data);

        // Save viewed history in localStorage (for recommendations).
        if (response.data) {
          const viewedHistory = JSON.parse(localStorage.getItem('viewedHistory') || '[]');
          // Remove duplicates.
          const filtered = viewedHistory.filter(item => item.id !== response.data.id);
          // Keep the most recent 20.
          const newHistory = [{
            id: response.data.id,
            name: response.data.name,
            image: response.data.image_url,
            price: response.data.sale_price,
            viewedAt: new Date().toISOString()
          }, ...filtered].slice(0, 20);
          localStorage.setItem('viewedHistory', JSON.stringify(newHistory));
        }

        // Behavior tracking.
        trackView(id);

        // GA4: view_item
        if (window.dataLayer && response.data) {
          window.dataLayer.push({ ecommerce: null }); // Clear previous ecommerce data
          window.dataLayer.push({
            event: 'view_item',
            ecommerce: {
              currency: 'TWD',
              value: response.data.sale_price,
              items: [{
                item_id: response.data.sku,
                item_name: response.data.name,
                item_category: response.data.category_name,
                price: response.data.sale_price,
                quantity: 1
              }]
            }
          });
        }

        // Facebook Pixel: ViewContent
        if (window.fbq && response.data) {
          window.fbq('track', 'ViewContent', {
            content_type: 'product',
            content_ids: [response.data.sku],
            content_name: response.data.name,
            content_category: response.data.category_name,
            value: response.data.sale_price,
            currency: 'TWD'
          });
        }

        // TikTok Pixel: ViewContent
        if (response.data) {
          trackTikTokViewContent(response.data);
        }

        // Review summary (optional).
        try {
          const reviewResponse = await api.get(`/reviews/product/${id}`);
          if (reviewResponse.data.success) {
            setReviewSummary(reviewResponse.data.data.summary);
          }
        } catch (error) {
          console.error('Failed to fetch review summary:', error);
        }
      } catch (error) {
        console.error('Failed to fetch product:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id]);

  const handleAddToCart = async () => {
    const specs = parseSpecs(product.specs);
    if (specs.length > 0) {
      const requiredSpecs = specs.filter(spec => spec.required);
      const missingRequired = requiredSpecs.filter(spec => !selectedOptions[spec.option_name]);

      if (missingRequired.length > 0) {
        toast.error(t('detail.select_required', { specs: missingRequired.map(s => s.option_name).join(' / ') }));
        return false;
      }
    }

    const optionsPrice = getOptionsPrice();
    const finalPrice = parseFloat(product.sale_price) + optionsPrice;
    const totalValue = finalPrice * quantity;

    const specInfo = specs.length > 0 ? {
      options: selectedOptions,
      optionsPrice: optionsPrice,
      finalPrice: finalPrice
    } : null;

    const success = await addToCart(product.id, quantity, specInfo);
    if (success) {
      trackCartAdd(product.id);

      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'add_to_cart',
          ecommerce: {
            currency: 'TWD',
            value: totalValue,
            items: [{
              item_id: product.sku,
              item_name: product.name,
              item_category: product.category_name,
              price: finalPrice,
              quantity: quantity
            }]
          }
        });
      }

      if (window.fbq) {
        window.fbq('track', 'AddToCart', {
          content_type: 'product',
          content_ids: [product.sku],
          content_name: product.name,
          value: totalValue,
          currency: 'TWD'
        });
      }

      // TikTok Pixel: AddToCart
      trackTikTokAddToCart(product, quantity);
    }

    return !!success;
  };

  const calculateDiscount = (original, sale) => {
    if (!original || original <= 0 || !sale) return 0;
    const discount = Math.round((1 - sale / original) * 100);
    return isNaN(discount) || !isFinite(discount) ? 0 : discount;
  };

  // Build a readable plain-text description (best effort).
  const getProductDescription = () => {
    const generateAutoDesc = () => {
      const discount = calculateDiscount(product.original_price, product.sale_price);
      const name = product?.name || 'Product';
      const original = product?.original_price ?? "";
      const sale = product?.sale_price ?? "";
      const pct = discount ? `${discount}% OFF` : "Limited offer";
      return `${pct} - ${name}. Original: ${original}. Sale: ${sale}.`;
    };

    if (product.description && product.description.trim()) {
      // Decode HTML entities and strip tags (best effort).
      let decoded = product.description;
      for (let i = 0; i < 3; i++) {
        decoded = decoded
          .replace(/&amp;/g, '&')
          .replace(/&lt;/g, '<')
          .replace(/&gt;/g, '>')
          .replace(/&quot;/g, '"')
          .replace(/&#39;/g, "'")
          .replace(/&#60;/g, '<')
          .replace(/&#62;/g, '>');
      }
      const cleaned = decoded.replace(/<[^>]*>/g, '').trim();
      const hasHtmlResidue = /[<>&]|img|src|alt|href/.test(cleaned);
      if (cleaned.length > 20 && !hasHtmlResidue) {
        return cleaned.substring(0, 300);
      }
    }
    return generateAutoDesc();
  };

  if (loading) {
    return (
      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
        <Container maxWidth="xl" sx={{ py: 3 }}>
          <Skeleton width={200} sx={{ mb: 2 }} />
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 1 }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <Skeleton width="80%" height={40} />
              <Skeleton width="40%" sx={{ mt: 2 }} />
              <Skeleton width="60%" sx={{ mt: 2 }} />
            </Grid>
          </Grid>
        </Container>
      </Box>
    );
  }

  if (!product) {
    return (
      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
        <Container maxWidth="xl" sx={{ py: 3 }}>
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h6" color="text.secondary">
              {t('detail.not_found')}
            </Typography>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/products')}
              sx={{ mt: 2 }}
            >
              {t('detail.back_to_list')}
            </Button>
          </Box>
        </Container>
      </Box>
    );
  }

  const discount = calculateDiscount(product.original_price, product.sale_price);
  const productDescription = getProductDescription();

  // Schema.org product structured data.
  const generateSchemaOrg = () => {
    const schema = {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": product.name,
      "description": productDescription,
      "image": parseImages(product),
      "sku": product.sku,
      "brand": {
        "@type": "Brand",
        "name": "URStore"
      },
      "offers": {
        "@type": "Offer",
        "url": window.location.origin + "/products/" + product.id,
        "priceCurrency": "TWD",
        "price": product.sale_price,
        "priceValidUntil": new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
        "availability": product.quantity > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
        "seller": {
          "@type": "Organization",
          "name": "URStore"
        }
      }
    };

    // Aggregate rating (optional).
    if (reviewSummary && reviewSummary.total_reviews > 0) {
      schema.aggregateRating = {
        "@type": "AggregateRating",
        "ratingValue": parseFloat(reviewSummary.average_rating).toFixed(2),
        "reviewCount": reviewSummary.total_reviews,
        "bestRating": "5",
        "worstRating": "1"
      };
    }

    return schema;
  };

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      <SEO
        title={product.name}
        description={productDescription}
        keywords={`${product.name}${product.category_name ? `,${product.category_name}` : ''}`}
        image={normalizeImageUrl(product.image_url, product.name)}
        url={`/products/${product.id}`}
        type="product"
        product={product}
      />
      <Container maxWidth="xl" sx={{ py: 3, pb: isMobile ? 16 : 3 }}>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(generateSchemaOrg())
          }}
        />

        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          sx={{ mb: 2 }}
        >
          <Link
            underline="hover"
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            color="inherit"
            onClick={() => navigate('/')}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
            {t('detail.breadcrumb_home')}
          </Link>
          <Link
            underline="hover"
            color="inherit"
            sx={{ cursor: 'pointer' }}
            onClick={() => navigate('/products')}
          >
            {t('detail.breadcrumb_products')}
          </Link>
          {product.category_name && (
            <Link
              underline="hover"
              color="inherit"
              sx={{ cursor: 'pointer' }}
              onClick={() => navigate(`/products?category=${product.category_id}`)}
            >
              {product.category_name}
            </Link>
          )}
          <Typography color="text.primary">{product.name}</Typography>
        </Breadcrumbs>

        <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          {(() => {
            const productImages = parseImages(product);
            return (
              <>
                <Paper sx={{ p: 2, position: 'relative', borderRadius: tplStyles.image.borderRadius }}>
                  {formatDiscountBadge(templateId, discount) && (
                    <Chip
                      icon={<LocalOfferIcon />}
                      label={formatDiscountBadge(templateId, discount)}
                      color="error"
                      sx={{
                        position: 'absolute', top: 16, left: 16,
                        fontWeight: tplStyles.badge.fontWeight,
                        fontFamily: tplStyles.badge.fontFamily || 'inherit',
                        fontSize: '1rem', zIndex: 1,
                        borderRadius: tplStyles.badge.borderRadius,
                      }}
                    />
                  )}
                  <Box
                    component="img"
                    src={productImages[selectedImageIndex]}
                    alt={product.name}
                    title={product.name}
                    loading="lazy"
                    sx={{
                      width: '100%',
                      height: 450,
                      objectFit: 'contain',
                      cursor: productImages.length > 1 ? 'pointer' : 'default'
                    }}
                    onClick={() => {
                      if (productImages.length > 1) {
                        setSelectedImageIndex((prev) => (prev + 1) % productImages.length);
                      }
                    }}
                    onError={(e) => onImageErrorSetFallback(e, product.name)}
                  />
                </Paper>

                {productImages.length > 1 && (
                  <Box sx={{
                    display: 'flex',
                    gap: 1,
                    mt: 2,
                    overflowX: 'auto',
                    pb: 1,
                    '&::-webkit-scrollbar': { height: 6 },
                    '&::-webkit-scrollbar-thumb': { bgcolor: '#ccc', borderRadius: 3 }
                  }}>
                    {productImages.map((img, idx) => (
                      <Box
                        key={idx}
                        onClick={() => setSelectedImageIndex(idx)}
                        sx={{
                          flexShrink: 0,
                          width: 72,
                          height: 72,
                          border: selectedImageIndex === idx ? `2px solid ${theme.palette.primary.main}` : '1px solid #ddd',
                          borderRadius: 1,
                          overflow: 'hidden',
                          cursor: 'pointer',
                          opacity: selectedImageIndex === idx ? 1 : 0.7,
                          transition: 'all 0.2s',
                          '&:hover': { opacity: 1, borderColor: 'primary.main' }
                        }}
                      >
                        <Box
                          component="img"
                          src={img}
                          alt={`${product.name} ${idx + 1}`}
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                          onError={(e) => onImageErrorSetFallback(e, product.name)}
                        />
                      </Box>
                    ))}
                  </Box>
                )}

                {productImages.length > 1 && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
                    {selectedImageIndex + 1} / {productImages.length} - {t('detail.click_to_switch')}
                  </Typography>
                )}
              </>
            );
          })()}

          <Box sx={{ mt: 2 }}>
            <SocialShare
              title={product.name}
              description={productDescription}
              url={`/products/${product.id}`}
              image={normalizeImageUrl(product.image_url, product.name)}
              product={product}
            />
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {product.category_name} | SKU: {product.sku}
          </Typography>

          <Typography variant="h5" component="h1" fontWeight="bold" gutterBottom>
            {product.name}
          </Typography>

          {reviewSummary && reviewSummary.total_reviews > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <StarRating
                value={reviewSummary.average_rating}
                readOnly
                size="small"
                totalReviews={reviewSummary.total_reviews}
              />
            </Box>
          )}

          <Paper
            sx={{
              p: 1.5,
              mb: 2,
              background: 'linear-gradient(135deg, #ff4444 0%, #ff6b6b 100%)',
              borderRadius: 2,
              color: 'white'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AccessTimeIcon />
                <Typography variant="body2" fontWeight="bold">{t('detail.countdown_title')}</Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Box sx={{ bgcolor: 'rgba(255,255,255,0.2)', px: 1.5, py: 0.5, borderRadius: 1 }}>
                  <Typography variant="h6" fontWeight="bold">{String(countdown.hours).padStart(2, '0')}</Typography>
                </Box>
                <Typography variant="h6">:</Typography>
                <Box sx={{ bgcolor: 'rgba(255,255,255,0.2)', px: 1.5, py: 0.5, borderRadius: 1 }}>
                  <Typography variant="h6" fontWeight="bold">{String(countdown.minutes).padStart(2, '0')}</Typography>
                </Box>
                <Typography variant="h6">:</Typography>
                <Box sx={{ bgcolor: 'rgba(255,255,255,0.2)', px: 1.5, py: 0.5, borderRadius: 1 }}>
                  <Typography variant="h6" fontWeight="bold">{String(countdown.seconds).padStart(2, '0')}</Typography>
                </Box>
              </Box>
            </Box>
          </Paper>

          {discount >= 50 && (
            <Alert severity="error" icon={<LocalOfferIcon />} sx={{ mb: 2 }}>
              {t('detail.flash_sale', { discount })}
            </Alert>
          )}

          <Box sx={{ my: 2 }}>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ textDecoration: 'line-through' }}
            >
              {t('detail.original_price', { price: product.original_price })}
            </Typography>
            <Typography
              variant="h4"
              color="error"
              sx={{
                fontWeight: tplStyles.price.fontWeight,
                fontFamily: tplStyles.price.fontFamily,
              }}
            >
              {t('detail.price', { price: product.sale_price })}
            </Typography>
            <Chip
              label={t('detail.you_save', { amount: Math.max(0, Number(product.original_price) - Number(product.sale_price)) })}
              color="success"
              size="small"
              sx={{ mt: 1 }}
            />
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ mb: 2 }}>
            {product.quantity > 10 ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip label={t('detail.in_stock')} color="success" size="small" />
                {product.sold_count > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    {t('detail.sold_count', { count: product.sold_count })}
                  </Typography>
                )}
              </Box>
            ) : product.quantity > 0 ? (
              <Paper
                sx={{
                  p: 1.5,
                  bgcolor: (t) => t.palette.custom?.hoverTint || '#fff3e0',
                  border: '2px solid',
                  borderColor: 'secondary.main',
                  borderRadius: 2
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <WhatshotIcon sx={{ color: '#ff5722', animation: 'pulse 1s infinite' }} />
                  <Typography variant="body1" fontWeight="bold" color="error">
                    {t('detail.stock_remaining', { count: product.quantity })}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={(product.quantity / 10) * 100}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    bgcolor: '#ffccbc',
                    '& .MuiLinearProgress-bar': {
                      bgcolor: product.quantity <= 3 ? '#f44336' : '#ff9800'
                    }
                  }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  {product.sold_count > 0 ? t('detail.sold_count', { count: product.sold_count }) : t('detail.limited_stock')}
                </Typography>
              </Paper>
            ) : (
              <Alert severity="error" sx={{ py: 0.5 }}>
                <Typography fontWeight="bold">{t('detail.out_of_stock')}</Typography>
              </Alert>
            )}
          </Box>

          {product.specs && parseSpecs(product.specs).length > 0 && (
            <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#fafafa' }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ color: '#333' }}>
                {t('detail.spec_selection')}
              </Typography>
              {parseSpecs(product.specs).map((spec, idx) => (
                <Box key={idx} sx={{ mb: 1.5 }}>
                    <Typography variant="body2" fontWeight="500" sx={{ mb: 0.5, color: '#555' }}>
                      {spec.option_name}
                      {spec.required && <Chip label={t('detail.required')} size="small" color="error" sx={{ ml: 1, height: 20, fontSize: '0.7rem' }} />}
                    </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                    {(spec.values || []).map((option, optIdx) => (
                      <Box key={optIdx} sx={{ position: 'relative' }}>
                        {option.image && selectedOptions[spec.option_name] === option.name && (
                          <Box
                            component="img"
                            src={option.image}
                            alt={option.name}
                            sx={{
                              position: 'absolute',
                              top: -60,
                              left: 0,
                              width: 50,
                              height: 50,
                              objectFit: 'cover',
                              borderRadius: 1,
                              border: `2px solid ${theme.palette.primary.main}`,
                              zIndex: 10
                            }}
                          />
                        )}
                        <Chip
                          label={
                            option.price > 0
                              ? `${option.name} (+$${Math.round(option.price)})`
                              : option.name
                          }
                          variant={selectedOptions[spec.option_name] === option.name ? 'filled' : 'outlined'}
                          color={selectedOptions[spec.option_name] === option.name ? 'primary' : 'default'}
                          onClick={() => setSelectedOptions(prev => ({
                            ...prev,
                            [spec.option_name]: option.name
                          }))}
                          size="medium"
                          sx={{
                            cursor: 'pointer',
                            fontSize: '0.875rem',
                            height: 32,
                            '&:hover': { borderColor: 'primary.main' }
                          }}
                        />
                      </Box>
                    ))}
                  </Box>
                </Box>
              ))}
              {getOptionsPrice() > 0 && (
                <Typography variant="body2" color="error" fontWeight="bold" sx={{ mt: 1, pt: 1, borderTop: '1px dashed #ddd' }}>
                  {t('detail.options_surcharge')}: +${getOptionsPrice()}
                </Typography>
              )}
            </Paper>
          )}

          {product.quantity > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Typography fontWeight={700}>{t('detail.quantity')}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', border: '1px solid #ddd', borderRadius: 1 }}>
                <IconButton
                  size="small"
                  onClick={() => setQuantity(q => Math.max(1, q - 1))}
                  disabled={quantity <= 1}
                >
                  <RemoveIcon />
                </IconButton>
                <Typography sx={{ px: 2, minWidth: 40, textAlign: 'center' }}>
                  {quantity}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setQuantity(q => Math.min(product.quantity, q + 1))}
                  disabled={quantity >= product.quantity}
                >
                  <AddIcon />
                </IconButton>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {t('detail.subtotal')}: ${Math.round((parseFloat(product.sale_price) + getOptionsPrice()) * quantity)}
              </Typography>
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <Button
              variant={tplStyles.button.variant}
              size="large"
              startIcon={<ShoppingCartIcon />}
              data-testid="add-to-cart"
              onClick={handleAddToCart}
              disabled={product.quantity <= 0}
              sx={{
                flex: 1,
                borderRadius: tplStyles.button.borderRadius,
                textTransform: tplStyles.button.textTransform,
                letterSpacing: tplStyles.button.letterSpacing || 'normal',
              }}
            >
              {tplStyles.button.addToCartText}
            </Button>
            <Button
              variant="outlined"
              size="large"
              color="error"
              data-testid="buy-now"
              onClick={async () => {
                const ok = await handleAddToCart();
                if (ok) navigate('/checkout');
              }}
              disabled={product.quantity <= 0}
              sx={{
                flex: 1,
                borderRadius: tplStyles.button.borderRadius,
                textTransform: tplStyles.button.textTransform,
                letterSpacing: tplStyles.button.letterSpacing || 'normal',
              }}
            >
              {tplStyles.button.buyNowText}
            </Button>
          </Box>

          <Paper variant="outlined" sx={{ mt: 2, p: 1.5, bgcolor: '#f8f9fa' }}>
            <Grid container spacing={1.5}>
              {product.weight && (
                <Grid item xs={12} sm={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <InventoryIcon sx={{ fontSize: 18, color: '#666' }} />
                  <Typography variant="body2" color="text.secondary">
                    {t('detail.weight', { weight: product.weight })}
                  </Typography>
                </Box>
              </Grid>
              )}
              <Grid item xs={12} sm={product.weight ? 4 : 6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <LocalShippingIcon sx={{ fontSize: 18, color: '#666' }} />
                  <Typography variant="body2" color="text.secondary">
                    {t('detail.cvs_shipping_info')}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={product.weight ? 4 : 6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <AssignmentReturnIcon sx={{ fontSize: 18, color: '#666' }} />
                  <Typography variant="body2" color="text.secondary">
                    {t('detail.return_policy')}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

        <Paper sx={{ p: 3, mt: 4, borderRadius: tplStyles.card.borderRadius }}>
          <Typography
            variant="h6"
            fontWeight="bold"
            gutterBottom
            sx={{ fontFamily: tplStyles.special.serifHeaders ? tplStyles.special.headerFontFamily : 'inherit' }}
          >
            {t('detail.description')}
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {product.description && product.description.includes('img') && (
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 0,
              mb: 2
            }}>
              {(() => {
                const decoded = product.description
                  .replace(/&lt;/g, '<')
                  .replace(/&gt;/g, '>')
                  .replace(/&amp;/g, '&')
                  .replace(/&quot;/g, '"')
                  .replace(/&#39;/g, "'");
                const imgRegex = /src\s*=\s*['"]([^'"]+)['"]/g;
                const images = [];
                let match;
                while ((match = imgRegex.exec(decoded)) !== null) {
                  let imgUrl = match[1];
                  if (imgUrl.startsWith('/image/')) {
                    imgUrl = `https://www.ymshop.com.tw${imgUrl}`;
                  }
                  images.push(imgUrl);
                }
                return images.map((img, idx) => (
                  <Box
                    key={idx}
                    component="img"
                    src={img}
                    alt={`${product.name} ${idx + 1}`}
                    loading="lazy"
                    sx={{
                      maxWidth: '100%',
                      width: 'auto',
                      maxHeight: 800,
                      height: 'auto',
                      display: 'block',
                      margin: '0 auto',
                      padding: 0
                    }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                ));
              })()}
            </Box>
          )}

          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ whiteSpace: 'pre-line', textAlign: 'center', mt: 2 }}
          >
            {productDescription}
          </Typography>
        </Paper>

        {/* ??閰? */}
        <Paper sx={{ p: 3, mt: 4, borderRadius: tplStyles.card.borderRadius }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{ fontFamily: tplStyles.special.serifHeaders ? tplStyles.special.headerFontFamily : 'inherit' }}
            >
              {t('detail.reviews')}
            </Typography>
            <Button
              variant={showReviewForm ? 'outlined' : 'contained'}
              startIcon={<RateReviewIcon />}
              onClick={() => setShowReviewForm(!showReviewForm)}
            >
              {showReviewForm ? t('detail.cancel') : t('detail.write_review')}
            </Button>
          </Box>
          <Divider sx={{ mb: 3 }} />

          {showReviewForm && (
            <Box sx={{ mb: 4 }}>
              <ReviewForm
                productId={product.id}
                onSuccess={(data) => {
                  setShowReviewForm(false);
                  // Refresh review summary.
                  api.get(`/reviews/product/${product.id}`).then(res => {
                    if (res.data.success) {
                      setReviewSummary(res.data.data.summary);
                    }
                  });
                }}
                onCancel={() => setShowReviewForm(false)}
              />
            </Box>
          )}

          {!showReviewForm && <ProductReviews productId={product.id} />}
        </Paper>

        {/* ?賊????刻 */}
        <RelatedProducts productId={product.id} limit={6} />

        {/* Mobile: sticky CTA above bottom navigation */}
        {isMobile && (
          <Paper
            elevation={10}
            sx={{
              position: 'fixed',
              left: 0,
              right: 0,
              bottom: 56,
              zIndex: 1200,
              borderTop: '1px solid',
              borderColor: 'divider',
              p: 1,
              bgcolor: 'background.paper',
            }}
          >
            <Container maxWidth="xl" sx={{ px: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ flex: 1, minWidth: 120 }}>
                  <Typography variant="caption" color="text.secondary">
                    {t('detail.subtotal')}
                  </Typography>
                  <Typography fontWeight={900} color="error">
                    ${Math.round((parseFloat(product.sale_price) + getOptionsPrice()) * quantity)}
                  </Typography>
                </Box>
                <Button
                  variant={tplStyles.button.variant}
                  size="medium"
                  onClick={handleAddToCart}
                  disabled={product.quantity <= 0}
                  sx={{ borderRadius: tplStyles.button.borderRadius, textTransform: tplStyles.button.textTransform }}
                >
                  {t('detail.add_to_cart')}
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  size="medium"
                  onClick={async () => {
                    const ok = await handleAddToCart();
                    if (ok) navigate('/checkout');
                  }}
                  disabled={product.quantity <= 0}
                  sx={{ borderRadius: tplStyles.button.borderRadius, textTransform: tplStyles.button.textTransform }}
                >
                  {t('detail.buy_now')}
                </Button>
              </Box>
            </Container>
          </Paper>
        )}
      </Container>
    </Box>
  );
};

export default ProductDetail;



